import { XIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  SCREENING_PROMPT_ACTION_CLASSNAME,
  SCREENING_PROMPT_KICKER_CLASSNAME,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { ActiveScreeningPrompt } from "@/types";

// Default screening-prompt content: candidate header + the four actions, sized
// to the notification popup it shares a corner with.
export const ScreeningHomeContent = ({
  activeScreen,
  isUpdatePending,
  onDismiss,
  onDismissAll,
  onMoveToReview,
  onNoShow,
  onOpenProfile,
  onStartReject,
  onStartReschedule,
}: {
  activeScreen: ActiveScreeningPrompt;
  isUpdatePending: boolean;
  onDismiss: () => void;
  // Only passed when this is the top card of a pile worth clearing at once.
  onDismissAll?: () => void;
  onMoveToReview: () => void;
  onNoShow: () => void;
  onOpenProfile: () => void;
  onStartReject: () => void;
  onStartReschedule: () => void;
}) => {
  const name = formatting.fullName(
    activeScreen.firstName,
    activeScreen.lastName,
    "Candidate",
  );
  const jobLabel = `${activeScreen.companyName} - ${activeScreen.jobTitle}`;

  return (
    <Column className="h-full justify-between gap-2 p-4">
      <Row className="items-start gap-2">
        <Column className="min-w-0 flex-1 gap-0.5">
          <Row className="min-w-0 items-center gap-1.5">
            <Text className={SCREENING_PROMPT_KICKER_CLASSNAME}>Screening</Text>
            <Text className="shrink-0 text-[10px] tabular-nums text-slate-400">
              {dates.formatTimeRangeShort(
                new Date(activeScreen.dateTime),
                activeScreen.durationMinutes,
              )}
            </Text>
          </Row>
          <Row className="min-w-0 items-baseline gap-1.5">
            <Button
              className="h-auto max-w-[55%] shrink-0 justify-start p-0 hover:bg-transparent"
              onClick={onOpenProfile}
              variant="ghost"
            >
              <Text
                className="text-sm font-semibold text-slate-900 hover:underline"
                truncate
              >
                {name}
              </Text>
            </Button>
            <Text className="shrink-0 text-[10px] text-slate-300">•</Text>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Row className="min-w-0 flex-1">
                    <Text
                      className="text-[11px] font-normal text-slate-500"
                      truncate
                    >
                      {jobLabel}
                    </Text>
                  </Row>
                </TooltipTrigger>
                <TooltipContent className="screening-prompt-portal z-[70]">
                  {jobLabel}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Row>
        </Column>
        <Row className="-mr-1 -mt-1 shrink-0 items-center gap-0.5">
          {onDismissAll ? (
            <Button
              // Matches the X it sits next to.
              className="h-5 px-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-700"
              onClick={onDismissAll}
              size="sm"
              variant="ghost"
            >
              Dismiss all
            </Button>
          ) : null}
          <Button
            className="h-7 w-7 shrink-0"
            onClick={onDismiss}
            size="icon"
            variant="ghost"
          >
            <XIcon className="h-4 w-4 text-slate-500" />
          </Button>
        </Row>
      </Row>
      <Row className="gap-1.5">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={SCREENING_PROMPT_ACTION_CLASSNAME}
                disabled={isUpdatePending}
                onClick={onNoShow}
                size="sm"
                variant="outlineSecondary"
              >
                No show
              </Button>
            </TooltipTrigger>
            <TooltipContent className="screening-prompt-portal z-[70]">
              Moves candidate to Engaged
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          className={SCREENING_PROMPT_ACTION_CLASSNAME}
          disabled={isUpdatePending}
          onClick={onStartReschedule}
          size="sm"
          variant="outlineSecondary"
        >
          Reschedule
        </Button>
        <Button
          className={SCREENING_PROMPT_ACTION_CLASSNAME}
          disabled={isUpdatePending}
          onClick={onStartReject}
          size="sm"
          variant="outlineDestructive"
        >
          Reject
        </Button>
        <Button
          className={SCREENING_PROMPT_ACTION_CLASSNAME}
          disabled={isUpdatePending}
          onClick={onMoveToReview}
          size="sm"
        >
          To Review
        </Button>
      </Row>
    </Column>
  );
};
